import { createContext, useContext, type Dispatch } from 'react';
import type { ColorScheme } from '../../visualization/types';

export type PropertiesTab = 'data' | 'format' | 'interaction';

/** Vista principal de la app; se refleja en `location.hash` para poder enlazarla. */
export type AppView = 'analysis' | 'connections' | 'assistant';

const VIEW_HASHES: Readonly<Record<AppView, string>> = {
  analysis: '#/analisis',
  connections: '#/conexiones',
  assistant: '#/asistente',
};

export const hashOfView = (view: AppView): string => VIEW_HASHES[view];

export function viewOfHash(hash: string): AppView {
  const match = (Object.entries(VIEW_HASHES) as [AppView, string][]).find(
    ([, value]) => value === hash,
  );
  return match?.[0] ?? 'analysis';
}

/**
 * Estado efimero de la interfaz: que tarjeta esta seleccionada, paneles abiertos, tema...
 * No forma parte del documento, no entra en el historial y no se serializa con el analisis.
 */
export interface UiState {
  readonly selectedCardId: string | null;
  readonly maximizedCardId: string | null;
  /** Tarjeta en el portapapeles interno (Ctrl+C); null si no hay copia. */
  readonly copiedCardId: string | null;
  readonly fieldSearch: string;
  readonly aiPanelOpen: boolean;
  readonly colorScheme: ColorScheme;
  readonly explorerOpen: boolean;
  readonly propertiesOpen: boolean;
  readonly propertiesTab: PropertiesTab;
  /** Modelo abierto en el workspace; la barra de estado indica si sus datos son reales. */
  readonly activeModelId: string | null;
  readonly activeView: AppView;
  /** Modelo que debe abrirse al volver a Analisis (p. ej. recien publicado). */
  readonly requestedModelId: string | null;
}

export type UiAction =
  | { readonly type: 'set-active-model'; readonly modelId: string | null }
  | { readonly type: 'set-active-view'; readonly view: AppView }
  | { readonly type: 'open-analysis'; readonly modelId: string }
  | { readonly type: 'select-card'; readonly cardId: string | null }
  | { readonly type: 'copy-card'; readonly cardId: string }
  | { readonly type: 'toggle-maximize'; readonly cardId: string }
  | { readonly type: 'set-field-search'; readonly query: string }
  | { readonly type: 'set-ai-panel'; readonly open: boolean }
  | { readonly type: 'toggle-color-scheme' }
  | { readonly type: 'toggle-explorer' }
  | { readonly type: 'toggle-properties' }
  | { readonly type: 'set-properties-tab'; readonly tab: PropertiesTab };

export function createUiState(
  colorScheme: ColorScheme = 'light',
  activeView: AppView = 'analysis',
): UiState {
  return {
    selectedCardId: null,
    maximizedCardId: null,
    copiedCardId: null,
    fieldSearch: '',
    aiPanelOpen: false,
    colorScheme,
    explorerOpen: true,
    propertiesOpen: true,
    propertiesTab: 'data',
    activeModelId: null,
    activeView,
    requestedModelId: null,
  };
}

export function uiReducer(state: UiState, action: UiAction): UiState {
  switch (action.type) {
    case 'set-active-view':
      return state.activeView === action.view ? state : { ...state, activeView: action.view };
    case 'open-analysis':
      return { ...state, activeView: 'analysis', requestedModelId: action.modelId };
    case 'set-active-model':
      return state.activeModelId === action.modelId
        ? state
        : { ...state, activeModelId: action.modelId };
    case 'select-card':
      return state.selectedCardId === action.cardId
        ? state
        : { ...state, selectedCardId: action.cardId };
    case 'copy-card':
      return state.copiedCardId === action.cardId
        ? state
        : { ...state, copiedCardId: action.cardId };
    case 'toggle-maximize':
      return {
        ...state,
        selectedCardId: action.cardId,
        maximizedCardId: state.maximizedCardId === action.cardId ? null : action.cardId,
      };
    case 'set-field-search':
      return { ...state, fieldSearch: action.query };
    case 'set-ai-panel':
      return { ...state, aiPanelOpen: action.open };
    case 'toggle-color-scheme':
      return { ...state, colorScheme: state.colorScheme === 'light' ? 'dark' : 'light' };
    case 'toggle-explorer':
      return { ...state, explorerOpen: !state.explorerOpen };
    case 'toggle-properties':
      return { ...state, propertiesOpen: !state.propertiesOpen };
    case 'set-properties-tab':
      return { ...state, propertiesTab: action.tab };
  }
}

export const UiStateContext = createContext<UiState | null>(null);
export const UiDispatchContext = createContext<Dispatch<UiAction> | null>(null);

export function useUiState(): UiState {
  const value = useContext(UiStateContext);
  if (value === null) throw new Error('useUiState requiere <UiStateProvider>.');
  return value;
}

export function useUiDispatch(): Dispatch<UiAction> {
  const value = useContext(UiDispatchContext);
  if (value === null) throw new Error('useUiDispatch requiere <UiStateProvider>.');
  return value;
}
